/**
 * Teta (Ask Teta) chat session id — first-party cookie with Max-Age.
 * UI reads/writes the cookie and sends sessionId in GraphQL; backend does not read this cookie.
 */

export const TETA_SESSION_COOKIE_NAME = "kivu_teta_sid";

const DEFAULT_MAX_AGE_SECONDS = 86400;
const MIN_MAX_AGE_SECONDS = 300;
const MAX_MAX_AGE_SECONDS = 604800;

const UUID_V4_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function safeGetCookie(name: string): string | null {
  if (typeof document === "undefined") {
    return null;
  }
  try {
    const prefix = `${name}=`;
    const parts = document.cookie.split(";");
    for (const part of parts) {
      const trimmed = part.trim();
      if (trimmed.startsWith(prefix)) {
        return decodeURIComponent(trimmed.slice(prefix.length));
      }
    }
    return null;
  } catch {
    return null;
  }
}

function isSecureCookieContext(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  return window.location.protocol === "https:";
}

/** Parsed Max-Age for the Teta session cookie (default 24h). */
export function getTetaSessionMaxAgeSeconds(): number {
  const raw = process.env.REACT_APP_TETA_SESSION_MAX_AGE_SECONDS?.trim();
  if (!raw) {
    return DEFAULT_MAX_AGE_SECONDS;
  }
  const seconds = Number.parseInt(raw, 10);
  if (!Number.isFinite(seconds)) {
    return DEFAULT_MAX_AGE_SECONDS;
  }
  return Math.min(MAX_MAX_AGE_SECONDS, Math.max(MIN_MAX_AGE_SECONDS, seconds));
}

export function isValidTetaSessionId(
  value: string | null | undefined
): boolean {
  if (!value) {
    return false;
  }
  return UUID_V4_PATTERN.test(value.trim());
}

/** Current session id from cookie, or null if missing / invalid. */
export function getTetaSessionIdFromCookie(): string | null {
  const value = safeGetCookie(TETA_SESSION_COOKIE_NAME);
  return isValidTetaSessionId(value) ? value!.trim() : null;
}

/** Persist session id with configured Max-Age. */
export function setTetaSessionCookie(sessionId: string): void {
  if (typeof document === "undefined" || !isValidTetaSessionId(sessionId)) {
    return;
  }
  const maxAge = getTetaSessionMaxAgeSeconds();
  const secure = isSecureCookieContext() ? "; Secure" : "";
  document.cookie = `${TETA_SESSION_COOKIE_NAME}=${encodeURIComponent(
    sessionId.trim()
  )}; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
}

/** Remove session cookie (new conversation on next open). */
export function clearTetaSessionCookie(): void {
  if (typeof document === "undefined") {
    return;
  }
  const secure = isSecureCookieContext() ? "; Secure" : "";
  document.cookie = `${TETA_SESSION_COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax${secure}`;
}

/** Read cookie or create a new RFC4122 v4 id and persist it. */
export function getOrCreateTetaSessionId(): string {
  const existing = getTetaSessionIdFromCookie();
  if (existing) {
    return existing;
  }
  const id =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : fallbackUuidV4();
  setTetaSessionCookie(id);
  return id;
}

function fallbackUuidV4(): string {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(
    ""
  );
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
